<script setup>
import JobEditForm from '@/components/JobEditForm.vue'
import { greeting } from '@/helpers/format'
import { useAuthStore } from '@/stores/auth'
import { useVisitStore } from '@/stores/visit'
import { useJobStore } from '@/stores/job'
import { usePaymentStore } from '@/stores/payment'
import dayjs from 'dayjs'

import 'dayjs/locale/he' // Import Hebrew locale for day names
import isBetween from 'dayjs/plugin/isBetween'
import { ref, onMounted, computed, watch } from 'vue' // Added 'watch'
import { useRouter } from 'vue-router'
import FollowUpJobs from '@/components/FollowUpJobs.vue'

dayjs.locale('he') // Set Day.js to use Hebrew locale
dayjs.extend(isBetween)

const router = useRouter()

const authStore = useAuthStore()
const visitStore = useVisitStore()
const jobStore = useJobStore()
const paymentStore = usePaymentStore ? usePaymentStore() : null

const showDialog = ref(false)
const viewMode = ref('week') // 'week', 'day', 'month'
const periodOffset = ref(0) // offset for week/day/month navigation
const isLoadingVisits = ref(false)

// Calculate the start and end of the displayed period
const displayedPeriodStart = computed(() => {
  if (viewMode.value === 'week') return dayjs().add(periodOffset.value, 'week').startOf('week')
  if (viewMode.value === 'day') return dayjs().add(periodOffset.value, 'day').startOf('day')
  if (viewMode.value === 'month') return dayjs().add(periodOffset.value, 'month').startOf('month')
  return dayjs()
})
const displayedPeriodEnd = computed(() => {
  if (viewMode.value === 'week') return dayjs().add(periodOffset.value, 'week').endOf('week')
  if (viewMode.value === 'day') return dayjs().add(periodOffset.value, 'day').endOf('day')
  if (viewMode.value === 'month') return dayjs().add(periodOffset.value, 'month').endOf('month')
  return dayjs()
})

// Fetch visits for the displayed period
const fetchVisitsForPeriod = async () => {
  isLoadingVisits.value = true
  await visitStore.readAllVisits({
    start: displayedPeriodStart.value.format('YYYY-MM-DD'),
    end: displayedPeriodEnd.value.format('YYYY-MM-DD')
  })
  isLoadingVisits.value = false
}

onMounted(async () => {
  fetchVisitsForPeriod()
  await jobStore.getAllJobs()
  await paymentStore.getAllPayments()
})

// Re-fetch visits when the period changes
watch([viewMode, periodOffset], fetchVisitsForPeriod)

const openJobsCount = computed(() => {
  return (jobStore.jobs || []).filter((job) => {
    const status = jobStore.getJobStatus(job)
    return status !== 'הושלמה' // רק עבודות שעדיין לא סגורות
  }).length
})

const monthlyIncome = computed(() => {
  if (!paymentStore || !paymentStore.payments) return 0
  const start = dayjs().startOf('month')
  const end = dayjs().endOf('month')
  return paymentStore.payments
    .filter((p) => dayjs(p.paymentDate).isBetween(start, end, null, '[]'))
    .reduce((sum, p) => sum + (p.amount || 0), 0)
})
const weeklyVisitsCount = computed(() => {
  const start = dayjs().startOf('week')
  const end = dayjs().endOf('week')
  return (visitStore.visits || []).filter(
    (v) =>
      dayjs(v.start).isBetween(start, end, null, '[]') && v.status !== 'בוטל' && v.status !== 'בוצע'
  ).length
})
</script>

<template>
  <div class="flex h-full flex-col justify-between gap-4 overflow-hidden px-2 pb-2">
    <div class="flex items-center justify-between gap-2">
      <h1 class="text-xl">{{ greeting() }} {{ authStore.user.name }}</h1>
      <Button
        label="צור עבודה חדשה"
        icon="pi pi-plus"
        @click="router.push('/jobs/new')"
        class="print:hidden"
        severity="success"
      />
    </div>
    <div class="grid grid-cols-3 gap-4 print:hidden">
      <router-link
        to="/jobs"
        class="flex cursor-pointer flex-col items-center rounded bg-white p-4 text-center shadow transition hover:bg-blue-50 focus:outline-none"
      >
        <span class="text-2xl font-bold text-blue-600">{{ openJobsCount }}</span>
        <span class="text-gray-600">עבודות פתוחות</span>
      </router-link>
      <router-link
        to="/payments"
        class="flex cursor-pointer flex-col items-center rounded bg-white p-4 text-center shadow transition hover:bg-green-50 focus:outline-none"
      >
        <span class="text-2xl font-bold text-green-600">₪{{ monthlyIncome }}</span>
        <span class="text-gray-600">הכנסות החודש</span>
      </router-link>
      <router-link
        to="/daily-plan"
        class="flex cursor-pointer flex-col items-center rounded bg-white p-4 text-center shadow transition hover:bg-purple-50 focus:outline-none"
      >
        <span class="text-2xl font-bold text-purple-600">{{ weeklyVisitsCount }}</span>
        <span class="text-gray-600">ביקורים השבוע</span>
      </router-link>
    </div>

    <FollowUpJobs />

    <RouterLink
      to="/daily-plan"
      class="flex cursor-pointer items-center justify-center gap-2 rounded bg-white p-4 text-center shadow transition hover:bg-purple-50 focus:outline-none"
    >
      <span class="text-2xl font-bold text-purple-600">לרשימת הביקורים</span>
      <i class="pi pi-calendar text-xl! text-purple-600"></i>
    </RouterLink>

    <!-- <VisitPeriodList /> -->
  </div>

  <Dialog
    v-model:visible="showDialog"
    :style="{ width: '350px' }"
    header="עבודה חדשה"
    modal
    class="print:hidden"
  >
    <JobEditForm @close="showDialog = false" />
  </Dialog>
</template>

<style>
@media print {
  body.print-body {
    direction: rtl;
  }
  .print-body .p-component {
    font-family: Arial, Helvetica, sans-serif;
  }
  .mb-6 {
    margin-bottom: 0;
  }
  li > span {
    margin-left: 0.5rem;
  }
}
</style>
